import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check } from 'lucide-react';
import { toast } from 'sonner';
import { WatchlistItem } from '@catalyst-iq/shared';
import { TickerIcon } from '../TickerIcon';
import { SignalSparkline } from '../signalSparkline';
import ConfirmDialog from '../shared/ConfirmDialog';
import { api } from '../../services/api';


export type WatchlistSortField =
  | 'ticker'
  | 'industry'
  | 'price'
  | 'today'
  | 'change'
  | 'rel_volume'
  | 'volume'
  | 'label'
  | 'note'
  | 'created_at';

interface WatchlistTableProps {
  watchlist: WatchlistItem[];
  onDelete: (id: string) => void;
  onUpdateNote: (id: string, note: string) => void;
  sortField: WatchlistSortField;
  sortOrder: 'asc' | 'desc';
  onSort: (field: WatchlistSortField) => void;
}

const TH = ({ children, right, onClick, className }: { children?: React.ReactNode; right?: boolean; onClick?: () => void; className?: string }) => (
  <th
    className={`px-5 py-3 text-[10.5px] font-semibold tracking-[0.08em] uppercase text-gray-400 dark:text-[#a3b6cc] whitespace-nowrap ${right ? 'text-right' : 'text-left'} ${onClick ? 'cursor-pointer select-none hover:text-gray-600 dark:hover:text-[#c2d0e0] group' : ''} ${className || ''}`}
    onClick={onClick}
  >
    {children}
  </th>
);

export default function WatchlistTable({ watchlist, onDelete, onUpdateNote, sortField, sortOrder, onSort }: WatchlistTableProps) {
  const renderArrow = (field: WatchlistSortField) => {
    if (sortField !== field) return <span className="opacity-20 ml-1 transition-opacity group-hover:opacity-60">↕</span>;
    return sortOrder === 'asc'
      ? <span className="text-[#10d98e] ml-1 font-sans">▲</span>
      : <span className="text-[#f5476a] ml-1 font-sans">▼</span>;
  };

  const SortLabel = ({ field, children, right }: { field: WatchlistSortField; children: React.ReactNode; right?: boolean }) => (
    <div className={`flex items-center pointer-events-none ${right ? 'justify-end' : ''}`}>
      {children} {renderArrow(field)}
    </div>
  );

  return (
    <div className="w-full min-w-0 rounded-2xl border border-gray-200 dark:border-[#1d2d47] bg-white dark:bg-[#0e1624] overflow-hidden shadow-sm">
      <div className="h-[3px] bg-gradient-to-r from-[#10d98e] via-[#06b6d4] to-[#4f8ff8]" />
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 dark:border-[#1d2d47] bg-gray-50/60 dark:bg-[#080c14]/50">
        <span className="text-[11px] font-semibold tracking-[0.12em] uppercase text-gray-500 dark:text-[#a3b6cc]">
          Watchlist
        </span>
        <span className="text-[11px] font-mono font-semibold text-gray-400 dark:text-[#a3b6cc]">
          {watchlist.length} tickers
        </span>
      </div>

      <div className="table-scroll">
        <table className="w-full text-[13px] border-collapse min-w-[1100px]">
          <thead>
            <tr className="border-b border-gray-100 dark:border-[#1d2d47]/70 bg-gray-50/50 dark:bg-[#0b111c]">
              <TH onClick={() => onSort('ticker')}><SortLabel field="ticker">Ticker</SortLabel></TH>
              <TH right onClick={() => onSort('industry')}><SortLabel field="industry" right>Industry</SortLabel></TH>
              <TH right onClick={() => onSort('price')}><SortLabel field="price" right>Price</SortLabel></TH>
              <TH right onClick={() => onSort('today')}><SortLabel field="today" right>Today</SortLabel></TH>
              <TH right onClick={() => onSort('change')}><SortLabel field="change" right>Change</SortLabel></TH>
              <TH right onClick={() => onSort('rel_volume')}><SortLabel field="rel_volume" right>Rel Vol</SortLabel></TH>
              <TH right onClick={() => onSort('volume')}><SortLabel field="volume" right>Volume</SortLabel></TH>
              <TH onClick={() => onSort('label')}><SortLabel field="label">Label</SortLabel></TH>
              <TH onClick={() => onSort('note')}><SortLabel field="note">Note</SortLabel></TH>
              <TH right>Actions</TH>
            </tr>
          </thead>
          <tbody>
            {watchlist.map((item, i) => (
              <WatchlistRow
                key={item.id}
                item={item}
                index={i}
                onDelete={onDelete}
                onUpdateNote={onUpdateNote}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface WatchlistRowProps {
  item: WatchlistItem;
  index: number;
  onDelete: (id: string) => void;
  onUpdateNote: (id: string, note: string) => void;
}

function WatchlistRow({ item, index, onDelete, onUpdateNote }: WatchlistRowProps) {
  const LABEL_CONFIG: Record<string, { label: string; pill: string; dot: string }> = {
    catalyst: { label: 'Catalyst', pill: 'bg-blue-500/10 text-blue-500 border-blue-500/20', dot: 'bg-blue-500' },
    momentum: { label: 'Momentum', pill: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20', dot: 'bg-amber-500' },
    none: { label: 'Unconfirmed', pill: 'bg-gray-500/10 text-gray-500 border-gray-500/20', dot: 'bg-gray-500' }
  };

  const getLabelCfg = (label: string): { label: string; pill: string; dot: string } => {
    const key = label?.toLowerCase();
    return LABEL_CONFIG[key] ?? LABEL_CONFIG['none'];
  };
  const [isEditing, setIsEditing] = useState(false);
  const [editedNote, setEditedNote] = useState(item.note || '');
  const [isSaving, setIsSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  
  const [hasActiveAlert, setHasActiveAlert] = useState((item as any).hasActiveAlert === true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const hasQuote = item.quote !== null && item.quote !== undefined;
  const price = hasQuote ? item.quote!.price : null;
  const change = hasQuote ? item.quote!.change_pct : null;
  const isPos = change !== null ? change >= 0 : true;
  const isEven = index % 2 === 0;
  const relVolume = hasQuote ? (item.quote as any).rel_volume ?? (item.quote as any).rel_vol ?? null : null; const volume = hasQuote ? (item.quote as any).volume : null;
  const label = (item as any).label || (hasQuote && (item.quote as any).label) || 'none';
  const labelCfg = getLabelCfg(label);

  useEffect(() => {
    setHasActiveAlert((item as any).hasActiveAlert === true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(item as any).hasActiveAlert]);

  useEffect(() => {
    if (!isEditing) setEditedNote(item.note || '');
  }, [item.note, isEditing]);

  const handleSave = async () => {
    if (editedNote.trim() === item.note) { setIsEditing(false); return; }
    setIsSaving(true);
    try {
      await onUpdateNote(item.id, editedNote);
      setIsEditing(false);
    } catch { /* handled upstream */ }
    finally { setIsSaving(false); }
  };

  const handleCancel = () => { setEditedNote(item.note || ''); setIsEditing(false); };

  return (
    <>
      <tr className={`
        border-b border-gray-50 dark:border-[#1d2d47]/40 last:border-0
        border-l-2 border-l-[#10d98e]/40
        transition-colors duration-100
        ${isEven ? '' : 'bg-gray-50/30 dark:bg-[#080c14]/20'}
        hover:bg-emerald-50/30 dark:hover:bg-[#10d98e]/5
      `}>
        {/* Ticker */}
        <td className="px-5 py-3.5">
          <div className="flex items-center gap-2.5">
            <TickerIcon ticker={item.ticker} size="sm" />
            <span className="font-mono font-bold text-[#4f8ff8] tracking-wide">{item.ticker}</span>
          </div>
        </td>

        {/* Industry */}
        <td className="px-5 py-3.5 text-right text-[12px] text-gray-600 dark:text-[#c2d0e0]">
          {(item as any).industry && (item as any).industry !== 'Unknown' ? (item as any).industry : '-'}
        </td>

        {/* Price */}
        <td className="px-5 py-3.5 text-right font-mono font-medium text-gray-800 dark:text-[#d5deea] tabular-nums">
          {price !== null
            ? `$${price.toFixed(2)}`
            : <span className="text-amber-500 dark:text-amber-400 text-[12px] font-sans font-normal italic">N/A</span>
          }
        </td>

        {/* Today (Mini Chart) */}
        <td className="px-5 py-3.5 align-middle">
          {price !== null && change !== null ? (
            <div className="w-20 h-5 flex items-center opacity-80">
              <SignalSparkline
                chartData={(item as any).chart_data || []}
                currentPrice={price}
                changePct={change}
              />
            </div>
          ) : '—'}
        </td>

        {/* Change */}
        <td className={`px-5 py-3.5 text-right font-mono font-semibold tabular-nums ${price === null ? 'text-gray-300 dark:text-[#a3b6cc]'
          : isPos ? 'text-emerald-600 dark:text-[#10d98e]'
            : 'text-red-500 dark:text-[#f5476a]'
          }`}>
          {change !== null
            ? <span className="inline-flex items-center justify-end gap-0.5">{isPos ? '▲' : '▼'}{Math.abs(change).toFixed(2)}%</span>
            : '—'
          }
        </td>

        {/* Rel Vol */}
        <td className="px-5 py-3.5 text-right font-mono tabular-nums text-gray-600 dark:text-[#c2d0e0]">
          {relVolume !== null ? `${relVolume.toFixed(1)}x` : '0.0x'}
        </td>

        {/* Volume */}
        <td className="px-5 py-3.5 text-right font-mono tabular-nums text-gray-600 dark:text-[#c2d0e0]">
          {volume !== null ? `${(volume / 1_000_000).toFixed(1)}M` : '0.0M'}
        </td>

        {/* Label */}
        <td className="px-5 py-3.5">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold border ${labelCfg.pill}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${labelCfg.dot}`} />
            {labelCfg.label}
          </span>
        </td>

        {/* Note */}
        <td className="px-5 py-3.5 max-w-[200px]">
          {isEditing ? (
            <input
              type="text"
              value={editedNote}
              onChange={e => setEditedNote(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') handleCancel(); }}
              className="w-full bg-gray-50 dark:bg-[#141e30] border border-gray-200 dark:border-[#1d2d47] rounded-lg px-3 py-1.5 text-[12px] text-gray-900 dark:text-[#f1f5fb] placeholder-gray-300 dark:placeholder-[#a3b6cc] focus:outline-none focus:border-[#4f8ff8] transition-colors"
              autoFocus
              placeholder="Add a note…"
            />
          ) : (
            <span className="text-[12px] text-gray-400 dark:text-[#a3b6cc] truncate block">
              {item.note || <span className="text-gray-300 dark:text-[#a3b6cc] italic">—</span>}
            </span>
          )}
        </td>

        {/* Actions */}
        <td className="px-5 py-3.5">
          <div className="flex items-center justify-end gap-1">
            {isEditing ? (
              <>
                <button onClick={handleSave} disabled={isSaving} className="px-2.5 py-1 bg-[#4f8ff8] text-white text-[11px] font-semibold rounded-lg hover:opacity-90 disabled:opacity-50">{isSaving ? '…' : 'Save'}</button>
                <button onClick={handleCancel} className="px-2.5 py-1 bg-gray-100 dark:bg-[#141e30] text-gray-600 dark:text-[#c2d0e0] text-[11px] font-semibold rounded-lg hover:bg-gray-200 dark:hover:bg-[#1a2744]">Cancel</button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className={`p-1.5 rounded-lg transition-all ${hasActiveAlert
                    ? 'text-amber-500 bg-amber-500/10 hover:bg-amber-500/20'
                    : 'text-gray-300 dark:text-[#a3b6cc] hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-500/10'
                    }`}
                  title={hasActiveAlert ? "Active Alert Set" : "Set Price Alert"}
                >
                  <span style={{ fontSize: '14px', display: 'block', lineHeight: '1' }}>
                    🔔
                  </span>
                  {hasActiveAlert && (
                    <span className="absolute top-1 right-1 flex h-1.5 w-1.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
                    </span>
                  )}
                </button>

                <button onClick={() => setIsEditing(true)} className="p-1.5 rounded-lg text-gray-300 dark:text-[#a3b6cc] hover:text-[#4f8ff8] hover:bg-blue-50 dark:hover:bg-[#4f8ff8]/10 transition-all" title="Edit note">
                  <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11.983 3.983a2.121 2.121 0 113 3L6 16.5l-4 1 1-4 8.983-8.517z" />
                  </svg>
                </button>
                

                {/* Delete */}
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="p-1.5 rounded-lg text-gray-300 dark:text-[#a3b6cc] hover:text-[#f5476a] hover:bg-red-50 dark:hover:bg-[#f5476a]/10 transition-all"
                  title="Remove"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 2h2M4 5h12M5 5l1 11a1 1 0 001 1h6a1 1 0 001-1l1-11" />
                  </svg>
                </button>
              </>
            )}
          </div>
        </td>
      </tr>

      <ConfirmDialog
        open={confirmDelete}
        tone="danger"
        title={`Remove ${item.ticker}?`}
        description="This ticker will be removed from your watchlist. You can add it again anytime."
        confirmLabel="Remove"
        cancelLabel="Cancel"
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => {
          setConfirmDelete(false);
          onDelete(item.id);
        }}
      />

      {/* Alert */}
      <AlertCreationModal
        isOpen={isModalOpen}
        ticker={item.ticker}
        currentPrice={price}
        onClose={() => setIsModalOpen(false)}
        onSuccess={() => {
          setHasActiveAlert(true);
          setIsModalOpen(false);
        }}
      />
    </>
  );
}

interface AlertModalProps {
  isOpen: boolean;
  ticker: string;
  currentPrice: number | null;
  onClose: () => void;
  onSuccess: () => void;
}

function AlertCreationModal({ isOpen, ticker, currentPrice, onClose, onSuccess }: AlertModalProps) {
  const [alertType, setAlertType] = useState<'price_above' | 'price_below' | 'change_pct_above'>('price_above');
  const [targetValue, setTargetValue] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uiError, setUiError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const [channels, setChannels] = useState<{ telegram: boolean; inApp: boolean }>({
    telegram: true,
    inApp: true,
  });

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChannelChange = (channel: 'telegram' | 'inApp') => {
    setChannels(prev => ({ ...prev, [channel]: !prev[channel] }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setUiError(null);
    setSuccessMsg(null);

    if (!targetValue || isNaN(Number(targetValue)) || Number(targetValue) <= 0) {
      setUiError('Please enter a valid value greater than 0');
      return;
    }

    const selectedChannels: Array<'telegram' | 'in_app'> = [];
    if (channels.inApp) selectedChannels.push('in_app');
    if (channels.telegram) selectedChannels.push('telegram');

    if (selectedChannels.length === 0) {
      setUiError('Please select at least one delivery channel');
      return;
    }

    setIsSubmitting(true);

    try {
      await api.createAlert({
        ticker,
        alert_type: alertType,
        target_value: Number(targetValue),
        channels: selectedChannels,
      });

      const msg = `Alert created for ${ticker}`;
      setSuccessMsg(msg);
      toast.success(msg, { description: 'Your alert is now active and monitoring.' });
      setTimeout(() => {
        onSuccess();
      }, 700);
    } catch (err: any) {
      const data = err?.response?.data;
      if (data?.error === 'duplicate_alert' || err?.response?.status === 409) {
        setUiError('This alert already exists in your system.');
        toast.error('This alert already exists.');
      } else {
        console.error('Error creating alert:', err);
        const message = data?.message || data?.error || 'Unable to create alert';
        setUiError(message);
        toast.error(message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 dark:bg-black/60 backdrop-blur-sm p-4 animate-fadeIn"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-[#0e1624] border border-gray-200 dark:border-[#1d2d47] rounded-2xl max-w-sm w-full p-6 shadow-xl text-left"
        style={{ direction: 'ltr' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-bold text-gray-900 dark:text-[#f1f5fb] mb-2">
          Create Alert for {ticker}
        </h3>

        {currentPrice !== null && (
          <p className="text-xs text-gray-500 dark:text-[#a3b6cc] mb-4">
            Current Market Price: <span className="font-mono font-semibold text-gray-700 dark:text-[#d5deea]">${currentPrice.toFixed(2)}</span>
          </p>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-[#a3b6cc] mb-1">Alert Type</label>
            <select
              value={alertType}
              onChange={(e) => setAlertType(e.target.value as any)}
              className="w-full bg-gray-50 dark:bg-[#161b26] border border-gray-300 dark:border-[#1d2d47] rounded-lg p-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-[#4f8ff8]"
            >
              <option value="price_above">Price goes above ($)</option>
              <option value="price_below">Price drops below ($)</option>
              <option value="change_pct_above">Daily change percentage above (%)</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-[#a3b6cc] mb-1">
              {alertType === 'change_pct_above' ? 'Target Percentage (%)' : 'Target Price ($)'}
            </label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              required
              value={targetValue}
              onChange={(e) => {
                setUiError(null);
                setTargetValue(e.target.value);
              }}
              placeholder={alertType === 'change_pct_above' ? 'e.g., 5.5' : 'e.g., 150.25'}
              className="w-full bg-gray-50 dark:bg-[#161b26] border border-gray-300 dark:border-[#1d2d47] rounded-lg p-2 text-sm font-mono text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#a3b6cc] focus:outline-none focus:border-[#4f8ff8]"
            />
          </div>

          <div className="border-t border-gray-100 dark:border-[#1d2d47] pt-3">
            <label className="block text-xs font-medium text-gray-500 dark:text-[#a3b6cc] mb-2">Delivery Channels</label>
            <div className="flex items-center gap-4 text-xs text-gray-700 dark:text-[#d5deea]">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={channels.inApp}
                  onChange={() => handleChannelChange('inApp')}
                  className="rounded border-gray-300 text-[#4f8ff8] focus:ring-0 dark:bg-[#161b26] dark:border-[#1d2d47]"
                />
                In-App Network
              </label>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={channels.telegram}
                  onChange={() => handleChannelChange('telegram')}
                  className="rounded border-gray-300 text-[#4f8ff8] focus:ring-0 dark:bg-[#161b26] dark:border-[#1d2d47]"
                />
                Telegram Bot
              </label>
            </div>
          </div>

          {uiError && (
            <div className="text-cat-red text-[11px] font-semibold text-center bg-[#f5476a]/10 p-2.5 rounded-xl border border-[#f5476a]/20">
              {uiError}
            </div>
          )}

          {successMsg && (
            <div className="text-emerald-700 dark:text-cat-green text-[11px] font-semibold text-center bg-emerald-50 dark:bg-[#10d98e]/10 p-2.5 rounded-xl border border-emerald-200 dark:border-[#10d98e]/20 flex items-center justify-center gap-1.5">
              <Check size={14} />
              {successMsg}
            </div>
          )}

          <div className="flex gap-2 pt-2 justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-xs bg-gray-100 dark:bg-[#141e30] text-gray-600 dark:text-[#c2d0e0] font-semibold rounded-lg hover:opacity-90 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !!successMsg}
              className="px-4 py-2 text-xs bg-[#4f8ff8] text-white font-semibold rounded-lg hover:opacity-90 disabled:opacity-50"
            >
              {isSubmitting ? 'Creating...' : 'Create Alert'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}